const {
  sanitizeDealerHost,
  resolveDealerHost,
} = require("./dealerHostHelper.ts");

const HEADER_GOL_DEALER_HOST = "Gol-Dealer-Host";
const HEADER_X_AMZN_TRACE_ID = "X-Amzn-Trace-Id";
const HEADER_X_FORWARDED_FOR = "X-Forwarded-For";
const HEADER_VALUE_MAX_LENGTH = 4096;
const TRACE_ID_MAX_LENGTH = 1024;

function generateUniqueSelfId() {
  const timestamp = Date.now().toString(36); // Base36 timestamp
  const random = Math.random().toString(36).substring(2, 10); // Random part
  return `${timestamp}-${random}`;
}

function normalizeHeaderValue(value, maxLength = HEADER_VALUE_MAX_LENGTH) {
  if (Array.isArray(value)) {
    return (
      value
        .map((headerValue) => normalizeHeaderValue(headerValue, maxLength))
        .find((normalizedHeaderValue) => normalizedHeaderValue !== null) || null
    );
  }

  if (
    typeof value !== "string" &&
    typeof value !== "number" &&
    typeof value !== "boolean"
  ) {
    return null;
  }

  const normalizedValue = String(value).trim();
  if (!normalizedValue) {
    return null;
  }

  if (
    normalizedValue.length > maxLength ||
    normalizedValue.includes("\n") ||
    normalizedValue.includes("\r")
  ) {
    return null;
  }

  return normalizedValue;
}

function appendUniqueSelfToTraceId(traceId) {
  const normalizedTraceId = normalizeHeaderValue(traceId, TRACE_ID_MAX_LENGTH);
  if (!normalizedTraceId) {
    return null;
  }

  const traceIdParts = normalizedTraceId
    .split(";")
    .map((tracePart) => tracePart.trim())
    .filter(Boolean);

  if (
    traceIdParts.length === 0 ||
    !traceIdParts[0].toLowerCase().startsWith("root=")
  ) {
    return null;
  }

  const traceIdWithoutSelf = traceIdParts.filter(
    (tracePart) => !tracePart.toLowerCase().startsWith("self=")
  );

  return `${traceIdWithoutSelf.join(";")};self=${generateUniqueSelfId()}`;
}

function getHeaderValue(headers, headerName) {
  if (!headers || typeof headers !== "object") {
    return null;
  }

  const normalizedHeaderName = headerName.toLowerCase();
  const matchingHeader = Object.keys(headers).find(
    (key) => key.toLowerCase() === normalizedHeaderName
  );

  if (!matchingHeader) {
    return null;
  }

  return normalizeHeaderValue(headers[matchingHeader]);
}

function getHostFromRequest(req) {
  const requestHeaders = req?.headers;
  const hostFromHeaders = getHeaderValue(requestHeaders, "host");

  if (hostFromHeaders) {
    return hostFromHeaders;
  }

  if (typeof req?.get === "function") {
    const hostFromGet = req.get("host");

    if (typeof hostFromGet === "string" && hostFromGet.trim().length > 0) {
      return hostFromGet.trim();
    }
  }

  if (typeof req?.hostname === "string" && req.hostname.trim().length > 0) {
    return req.hostname.trim();
  }

  return "";
}

function getForwardedHeadersFromRequest(req = null) {
  const requestHeaders = req?.headers;

  const incomingDealerHost = getHeaderValue(
    requestHeaders,
    HEADER_GOL_DEALER_HOST
  );
  const forwardedFor = getHeaderValue(requestHeaders, HEADER_X_FORWARDED_FOR);
  const amznTraceId = getHeaderValue(requestHeaders, HEADER_X_AMZN_TRACE_ID);
  const host = getHostFromRequest(req);

  const dealerHost = resolveDealerHost({
    incomingDealerHost,
    host,
  });

  const result = {};

  if (dealerHost) {
    result[HEADER_GOL_DEALER_HOST] = dealerHost;
  }

  if (forwardedFor) {
    result[HEADER_X_FORWARDED_FOR] = forwardedFor;
  }

  const traceIdWithSelf = appendUniqueSelfToTraceId(amznTraceId);
  if (traceIdWithSelf) {
    result[HEADER_X_AMZN_TRACE_ID] = traceIdWithSelf;
  }

  return result;
}

function getBrowserForwardedHeaders() {
  if (typeof window === "undefined") {
    return {};
  }

  const dealerHost = resolveDealerHost({
    host: window.location?.host,
  });

  const result = {};

  if (dealerHost) {
    result[HEADER_GOL_DEALER_HOST] = dealerHost;
  }

  return result;
}

module.exports = {
  HEADER_GOL_DEALER_HOST,
  HEADER_X_AMZN_TRACE_ID,
  HEADER_X_FORWARDED_FOR,
  sanitizeDealerHost,
  resolveDealerHost,
  getForwardedHeadersFromRequest,
  getBrowserForwardedHeaders,
};
